import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

const UnifiedDataTable = ({ data, onFlagClick, onDeleteClick, onFlagEdit }) => {
  const navigate = useNavigate();

  const FLAGS = [
    { id: 'reroute', label: 'Reroute', color: 'bg-accent-purple text-white', icon: 'RefreshCw' },
    { id: 'sick', label: 'Sick', color: 'bg-error text-white', icon: 'Heart' },
    { id: 'apd_pd', label: 'APD/PD', color: 'bg-accent-blue text-white', icon: 'Calendar' },
    { id: '23k', label: '23K', color: 'bg-accent-cyan text-white', icon: 'DollarSign' },
    { id: 'ws', label: 'WS', color: 'bg-accent-green text-white', icon: 'Clock' },
    { id: 'gs', label: 'GS', color: 'bg-success text-white', icon: 'CheckCircle' },
    { id: 'sl', label: 'SL', color: 'bg-warning text-black', icon: 'AlertCircle' },
    { id: 'qs', label: 'QS', color: 'bg-accent-pink text-white', icon: 'HelpCircle' },
    { id: 'vac', label: 'VAC', color: 'bg-primary text-white', icon: 'Palmtree' },
    { id: 'other', label: 'Other', color: 'bg-muted text-foreground', icon: 'MoreHorizontal' }
  ];

  const getStatusBadge = (status) => {
    const configs = {
      valid: { label: 'Valid', color: 'bg-success/20 text-success border-success/30', icon: 'CheckCircle' },
      warning: { label: 'Warning', color: 'bg-warning/20 text-warning border-warning/30', icon: 'AlertTriangle' },
      error: { label: 'Error', color: 'bg-error/20 text-error border-error/30', icon: 'XCircle' }
    };
    return configs[status] || configs.valid;
  };

  const getTypeConfig = (type) => {
    return type === 'rotation' 
      ? { icon: 'Plane', color: 'text-accent-cyan', label: 'Rotation' }
      : { icon: 'FileText', color: 'text-accent-purple', label: 'Timecard' };
  };

  const handleRowClick = (item) => {
    if (item.type === 'rotation') {
      // Use rotation_number (identifier) not database ID - GOLDEN RULE 0
      navigate(`/rotation-breakdown?id=${item.identifier}`);  // ✅ Rotation Breakdown page
    } else {
      navigate(`/timecard-detail?id=${item.id}`);
    }
  };

  return (
    <div className="bg-card rounded-lg border border-accent-cyan/20 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-card-alt border-b border-accent-cyan/20">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Identifier
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Employee
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Dates
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Credit Hours
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Total Pay Hours
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Link Status
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Flags
              </th>
              <th className="px-4 py-3 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-accent-cyan/10">
            {data.map((item) => {
              const statusBadge = getStatusBadge(item.status);
              const typeConfig = getTypeConfig(item.type);
              const itemFlags = item.flags || [];

              return (
                <tr 
                  key={`${item.type}-${item.id}`}
                  className="hover:bg-accent-cyan/5 transition-colors cursor-pointer"
                  onClick={() => handleRowClick(item)}
                >
                  {/* Type Column */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Icon name={typeConfig.icon} className={`w-4 h-4 ${typeConfig.color}`} />
                      <span className="text-xs text-muted-foreground">{typeConfig.label}</span>
                    </div>
                  </td>

                  {/* Identifier Column */}
                  <td className="px-4 py-3">
                    <span className="font-semibold text-foreground">{item.identifier}</span>
                  </td>

                  {/* Employee Column */}
                  <td className="px-4 py-3">
                    <div className="text-sm text-foreground">{item.employeeName}</div>
                    <div className="text-xs text-muted-foreground">{item.employeeId}</div>
                  </td>

                  {/* Dates Column */}
                  <td className="px-4 py-3">
                    <div className="text-sm text-foreground font-mono">
                      {item.startDate}
                    </div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {item.endDate}
                    </div>
                  </td>

                  {/* Credit Hours Column */}
                  <td className="px-4 py-3 text-right">
                    {item.creditHours !== null ? (
                      <span className="text-sm text-foreground font-semibold">{item.creditHours.toFixed(2)}</span>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </td>

                  {/* Total Pay Hours Column */}
                  <td className="px-4 py-3 text-right">
                    {item.totalPayHours !== null ? (
                      <span className="text-sm text-foreground font-semibold">{item.totalPayHours.toFixed(2)}</span>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </td>

                  {/* Link Status Column */}
                  <td className="px-4 py-3">
                    <div className="flex justify-center">
                      {item.linked ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-success/20 text-success border border-success/30">
                          <Icon name="Link" className="w-3 h-3" />
                          Linked
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-muted/20 text-muted-foreground border border-muted/30">
                          <Icon name="LinkOff" className="w-3 h-3" />
                          Unlinked
                        </span>
                      )}
                    </div>
                  </td>

                  {/* Status Column */}
                  <td className="px-4 py-3">
                    <div className="flex justify-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${statusBadge.color}`}>
                        {statusBadge.label}
                      </span>
                    </div>
                  </td>

                  {/* Flags Column - Only for rotations */}
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    {item.type === 'rotation' ? (
                      <div className="flex flex-wrap gap-1 justify-center max-w-[200px]">
                        {itemFlags.length > 0 ? (
                          itemFlags.map((flagId) => {
                            const flag = FLAGS.find(f => f.id === flagId);
                            if (!flag) return null;
                            return (
                              <button
                                key={flagId}
                                onClick={() => onFlagEdit(item, flagId)}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${flag.color} hover:opacity-80 transition-opacity cursor-pointer`}
                                title="Click to edit or delete flag"
                              >
                                <Icon name={flag.icon} className="w-3 h-3" />
                                {flag.label}
                              </button>
                            );
                          })
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>

                  {/* Actions Column */}
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-2">
                      {item.type === 'rotation' && (
                        <>
                          <button
                            onClick={() => onFlagClick(item)}
                            className="p-1.5 rounded-lg hover:bg-accent-cyan/10 text-accent-cyan transition-colors"
                            title="Add Flag"
                          >
                            <Icon name="Tag" className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/audit?type=rotation&number=${item.identifier}`)}
                            className="p-1.5 rounded-lg hover:bg-accent-purple/10 text-accent-purple transition-colors"
                            title="Version History"
                          >
                            <Icon name="History" className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleRowClick(item)}
                        className="p-1.5 rounded-lg hover:bg-primary/10 text-primary transition-colors"
                        title="View Details"
                      >
                        <Icon name="Eye" className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDeleteClick(item)}
                        className="p-1.5 rounded-lg hover:bg-error/10 text-error transition-colors"
                        title="Delete"
                      >
                        <Icon name="Trash2" className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {data.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">
          <Icon name="Database" className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No data found</p>
        </div>
      )}
    </div>
  );
};

export default UnifiedDataTable;
